import React, { useEffect, useRef, useState } from "react";
import { api } from "../../api";

/**
 * TEMPLATE — the editor, the variable chips, and the rendered preview.
 *
 * The preview is rendered BY THE SERVER, against a real recent lead and live
 * calendar slots. That is the whole point of it: a preview rendered in the
 * browser would be a second implementation of the substitution rules, and the
 * value of what is on screen is that it is what the lead receives.
 *
 * It also means the preview can REFUSE, and showing that refusal is as
 * important as showing the email. `inbound_summary` fills "I read your note
 * about ___", and a lead who filled the form with no message gives us nothing
 * to quote — so the render fails and says which variable was missing, rather
 * than sending a broken sentence or inventing a plausible one. (See the header
 * of src/automations/template.js: a fabricated quote in Gabriel's name is the
 * failure mode worth designing out.)
 */

const CHIP_DOC = {
  first_name: "Falls back to “there”",
  company: "Falls back to “you”",
  my_first_name: "Yours",
  inbound_summary: "Their own words, cut short. No fallback — the send is blocked without it",
  availability: "Exact times, or a described window, or the days you could move something",
  their_point:
    "Reads their note for a referral, a worry or a question, and answers it. Empty for most notes, and then it takes its own line out",
  sign_off: "One of “Best,” “Cheers,” or “Thanks,”, picked per send",
  booking_link: "This lead's own booking page link, their name already filled in",
  slot_1: "Empty slots take their own line with them",
  slot_2: "Empty slots take their own line with them",
  slot_3: "Empty slots take their own line with them",
};

/** The same cohorts the Rules tab offers, in words rather than column values. */
const COHORT_LABELS = {
  recent: "Most recent",
  call_scheduled: "Call scheduled",
  in_progress: "In progress",
  proposal_sent: "Pricing sent",
  evaluating: "Evaluating",
  agreement: "Agreement",
  won: "Became clients",
};

/*
 * WHO THE PREVIEW RENDERS AGAINST. Asked for 2026-09-08: "I'd like to be able
 * to test this with different leads. Maybe allow me here to change who the
 * template is tested on."
 *
 * Two selects rather than a search box, because the useful question is not
 * "which lead" but "which KIND of lead", and there are only ever a few answers
 * worth trying. The cohort list is the Rules tab's vocabulary, so a lead that
 * became a client is one click from here.
 *
 * WHAT EACH ROW SHOWS IS WHAT DRIVES THE TEMPLATE, not what identifies the
 * person. The country decides exact times against prose, and the whole
 * US-versus-single-zone branch is invisible until you render one of each. A
 * lead with no note is marked, because that lead BLOCKS the render, and being
 * able to pick one deliberately is how he sees that refusal rather than meeting
 * it in the log.
 */
function LeadPicker({ contactId, onPick }) {
  const [cohort, setCohort] = useState("recent");
  const [options, setOptions] = useState(null);
  const [failed, setFailed] = useState(null);

  useEffect(() => {
    let live = true;
    setFailed(null);
    api(`/automations/leads/options?cohort=${encodeURIComponent(cohort)}&limit=40`)
      .then((result) => {
        if (!live) return;
        setOptions(result);
      })
      .catch((err) => {
        if (live) setFailed(err.message);
      });
    return () => {
      live = false;
    };
  }, [cohort]);

  if (failed) return <span className="crm-hint crm-hint--warn">{failed}</span>;

  const leads = (options && options.leads) || [];
  const cohorts = (options && options.cohorts) || [];

  return (
    <div className="crm-auto-leadpick">
      {cohorts.length > 1 && (
        <select
          className="crm-select"
          value={cohort}
          onChange={(event) => setCohort(event.target.value)}
          aria-label="Which leads to choose from"
        >
          {cohorts.map((name) => (
            <option key={name} value={name}>
              {COHORT_LABELS[name] || name}
            </option>
          ))}
        </select>
      )}
      <select
        className="crm-select"
        value={contactId || ""}
        onChange={(event) => onPick(event.target.value || null)}
        aria-label="Which lead to render against"
      >
        <option value="">Most recent lead</option>
        {leads.map((lead) => (
          <option key={lead.contact_id} value={lead.contact_id}>
            {[lead.name, lead.company, lead.country].filter(Boolean).join(" · ")}
            {lead.has_message ? "" : "  (no note)"}
          </option>
        ))}
      </select>
    </div>
  );
}

export default function TemplateTab({ id, config, patch, meta, contactId, onPickContact }) {
  const template = config.template;
  const [variant, setVariant] = useState("A");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const bodyRef = useRef(null);
  const latest = useRef(0);

  const current = variant === "B" && template.variant_b ? template.variant_b : template.variant_a;
  const key = variant === "B" && template.variant_b ? "variant_b" : "variant_a";

  const setPart = (part, value) => {
    patch("template", { [key]: { ...current, [part]: value } });
  };

  // Debounced, because this re-reads the calendar.
  useEffect(() => {
    const token = ++latest.current;
    const timer = setTimeout(() => {
      setLoading(true);
      api(`/automations/${id}/template/preview`, {
        method: "POST",
        // `contact_id` null means "the most recent lead", which is what the
        // server already defaulted to before there was a picker.
        body: JSON.stringify({ config, variant, contact_id: contactId || null }),
      })
        .then((result) => {
          if (token === latest.current) setPreview(result);
        })
        .catch((err) => {
          if (token === latest.current) setPreview({ errors: [err.message] });
        })
        .finally(() => {
          if (token === latest.current) setLoading(false);
        });
    }, 500);
    return () => clearTimeout(timer);
  }, [id, config, variant, contactId]);

  /**
   * Insert a variable at the cursor rather than appending it. A chip that always
   * appended would be useless for the one thing chips are for — dropping a
   * variable into a sentence you are already writing.
   */
  const insert = (name) => {
    const node = bodyRef.current;
    const token = `{{${name}}}`;
    if (!node) {
      setPart("body", `${current.body}${token}`);
      return;
    }
    const start = node.selectionStart ?? current.body.length;
    const end = node.selectionEnd ?? start;
    const next = `${current.body.slice(0, start)}${token}${current.body.slice(end)}`;
    setPart("body", next);
    // Put the caret after what was just inserted, on the next frame, once React
    // has written the new value.
    requestAnimationFrame(() => {
      node.focus();
      node.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const variables = meta?.variables || [];

  return (
    <div className="crm-auto-template">
      <div className="crm-auto-template__left">
        <div className="crm-auto-variants">
          <button
            type="button"
            className={`crm-auto-variant${variant === "A" ? " is-active" : ""}`}
            onClick={() => setVariant("A")}
          >
            Variant A
          </button>
          <button
            type="button"
            className={`crm-auto-variant${variant === "B" ? " is-active" : ""}`}
            disabled={!template.variant_b}
            onClick={() => setVariant("B")}
          >
            Variant B
          </button>
          {template.ab_enabled && template.variant_b ? (
            <span className="crm-hint">
              50 / 50 split, alternating every send · compared on the Log tab
            </span>
          ) : (
            <span className="crm-hint">A/B is off</span>
          )}
        </div>

        <section className="crm-card crm-auto-editor">
          <label className="crm-auto-subject">
            <span className="crm-label">Subject</span>
            <input
              className="crm-input"
              value={current.subject}
              onChange={(event) => setPart("subject", event.target.value)}
            />
          </label>

          <textarea
            ref={bodyRef}
            className="crm-auto-body"
            rows={18}
            value={current.body}
            onChange={(event) => setPart("body", event.target.value)}
            aria-label="Email body"
          />

          <footer className="crm-auto-editor__foot">
            {/*
              The mockup's footer says "Gmail appends your signature". It does
              not — Gmail's API appends nothing, and sendViaGmail's signature is
              opt-in precisely so an unattended sender's wire format does not
              change as a side effect. So it is a toggle, and this says which.
            */}
            <label className="crm-auto-inline">
              <input
                type="checkbox"
                checked={template.signature}
                onChange={(event) => patch("template", { signature: event.target.checked })}
              />
              <span>Add your Gmail signature</span>
            </label>
            <span className="crm-hint">
              {template.signature
                ? "Sent as plain text plus an HTML part carrying your signature."
                : "Sent as plain text. Nothing is appended."}
            </span>
          </footer>
        </section>

        <section className="crm-card crm-auto-vars">
          <h3>Insert variable</h3>
          <div className="crm-auto-varlist">
            {variables.map((variable) => (
              <button
                key={variable.name}
                type="button"
                className="crm-auto-var"
                onClick={() => insert(variable.name)}
                title={CHIP_DOC[variable.name] || variable.describe}
              >
                {variable.name}
              </button>
            ))}
          </div>
          <p className="crm-hint">
            A slot line with nothing to say disappears, taking its own line with it — so a fully booked week sends a
            tidy email with the booking link instead of three blank bullets.
          </p>
          <p className="crm-hint">
            To put a link behind words, wrap them like <code>[grab a time here]({"{{booking_link}}"})</code>.
          </p>
        </section>

        <section className="crm-card">
          <header className="crm-auto-cardhead">
            <h3>A/B second variant</h3>
            <label className="crm-auto-switch">
              <input
                type="checkbox"
                checked={template.ab_enabled}
                onChange={(event) => {
                  const on = event.target.checked;
                  patch("template", {
                    ab_enabled: on,
                    // Turning it on with nothing to send would be a config the
                    // server refuses, so switching it on seeds B from A.
                    variant_b: on && !template.variant_b ? { ...template.variant_a } : template.variant_b,
                  });
                  if (on) setVariant("B");
                }}
              />
              <span className="crm-hint">on</span>
            </label>
          </header>
          <p className="crm-hint">
            Splits sends evenly and alternates, so the two samples stay the same size — which is what makes the
            reply-time comparison on the Log tab mean anything.
          </p>
          {template.ab_enabled && template.variant_b && (
            <button
              type="button"
              className="crm-linkact crm-btn--danger-hover"
              onClick={() => patch("template", { ab_enabled: false, variant_b: null })}
            >
              Remove variant B
            </button>
          )}
        </section>

        <section className="crm-card">
          <h3>Notify</h3>
          <label className="crm-auto-inline">
            <input
              type="checkbox"
              checked={template.slack_notify}
              onChange={(event) => patch("template", { slack_notify: event.target.checked })}
            />
            <span>
              Post to Slack <b>{template.slack_channel}</b> when one goes out
            </span>
          </label>
        </section>
      </div>

      <div className="crm-auto-template__right">
        <section className={`crm-card crm-auto-mailpreview${loading ? " is-loading" : ""}`}>
          <header className="crm-auto-cardhead">
            <h3>Preview</h3>
            <LeadPicker contactId={contactId} onPick={onPickContact} />
          </header>
          {preview?.lead && (
            <p className="crm-hint crm-auto-leadpick__as">
              Rendering as {preview.lead.name}
              {preview.lead.company ? ` · ${preview.lead.company}` : ""}
            </p>
          )}

          {preview?.errors?.length ? (
            <p className="crm-hint crm-hint--warn">{preview.errors.join(" ")}</p>
          ) : !preview ? (
            <p className="crm-hint">Rendering…</p>
          ) : !preview.rendered.ok ? (
            /*
              A REFUSAL, SHOWN AS ONE. This is not an error in the template — it
              is the template declining to invent something. Saying which
              variable is missing is what makes it actionable: either this lead
              has no message, or the variable should come out of the template.
            */
            <div className="crm-auto-refusal">
              <b>This lead would not get a reply.</b>
              <p>
                The template needs <code>{preview.rendered.missing.join(", ")}</code> and this one has none. Nothing
                is guessed and nothing half-written is sent — the Log records it as blocked and you answer them
                yourself.
              </p>
            </div>
          ) : (
            <>
              <dl className="crm-auto-mailhead">
                <dt>From</dt>
                <dd>{preview.send_from || "your Gmail account"}</dd>
                <dt>To</dt>
                <dd>
                  {preview.lead.name} &lt;{preview.lead.email}&gt;
                </dd>
                <dt>Subject</dt>
                <dd>
                  <b>{preview.rendered.subject}</b>
                </dd>
              </dl>
              {preview.rendered.html ? (
                // Built server-side from escaped text; the only markup in it is <br> and the template's links.
                <div className="crm-auto-mailbody" dangerouslySetInnerHTML={{ __html: preview.rendered.html }} />
              ) : (
                <pre className="crm-auto-mailbody">{preview.rendered.body}</pre>
              )}
              <footer className="crm-auto-mailfoot">
                {preview.slots?.length ? (
                  <span className="crm-hint">
                    {preview.slots.length} {preview.slots.length === 1 ? "slot" : "slots"} pulled from your calendar
                    just now, in {preview.timezone}
                  </span>
                ) : (
                  <span className="crm-hint">
                    {preview.timezone
                      ? "No free slots this week, so the email offers the booking link."
                      : "This lead's timezone is unknown, so no times are offered — only the booking link. Times are never sent in Israel time."}
                  </span>
                )}
              </footer>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
